import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useProducts,
  useCreateProject,
  type ProductSummary,
} from "@/shared/api/queries";
import { useSessionStore } from "@/shared/session";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import { IsaleModal } from "@/shared/components/Modal";
import { useToast } from "@/shared/components/Toast";
import { cn } from "@/lib/utils";

/**
 * Global "create clip" modal — mounted ONCE in App.tsx (like AuthExpiredModal)
 * and opened from anywhere via the `isale:new-clip` window event.
 *
 * Consolidates every create-clip entry (Dashboard header CTA, Projects header/
 * empty CTA, and the removed per-page floating FABs + the cradle center FAB)
 * into one modal — mirroring the mobile-native context-aware center FAB which
 * opens NewClipModal directly (apps/isale-mobile/app/(tabs)/_layout.tsx). The
 * web cradle FAB (MobileBottomTab) dispatches `isale:new-clip` on every tab
 * except Products (which dispatches `isale:new-product`).
 *
 * Backend constraint: only `status=ready` products can become a project (412
 * otherwise) — the picker filters to ready-only and offers a "go to Products"
 * affordance when none qualify yet.
 */
export function NewClipModal() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const workspaceId = useSessionStore((s) => s.session?.workspaceId);

  const [visible, setVisible] = useState(false);
  const [productId, setProductId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [duration, setDuration] = useState<15 | 30 | 45 | 60>(30);

  const products = useProducts(workspaceId);
  const createProject = useCreateProject();

  // Open on the global `isale:new-clip` event (dispatched by the cradle FAB +
  // Dashboard/Projects CTAs). Mirrors AuthExpiredModal's event-driven mount.
  useEffect(() => {
    function onOpen() {
      setVisible(true);
    }
    window.addEventListener("isale:new-clip", onOpen);
    return () => window.removeEventListener("isale:new-clip", onOpen);
  }, []);

  const readyProducts = useMemo<ProductSummary[]>(
    () => (products.data?.items ?? []).filter((p) => p.status === "ready"),
    [products.data],
  );

  // Default the clip name to the product name when selection changes so the
  // user only has to type when they want something custom.
  function handleProductSelect(id: string) {
    setProductId(id);
    const found = readyProducts.find((p) => p.id === id);
    if (found && !name.trim()) setName(found.name);
  }

  function reset() {
    setProductId("");
    setName("");
    setDuration(30);
  }

  function close() {
    setVisible(false);
    reset();
  }

  async function handleSubmit() {
    if (!workspaceId || !productId) return;
    if (name.trim().length < 2) {
      toast.warning(t("projects.newNameLabel"));
      return;
    }
    try {
      const created = await createProject.mutateAsync({
        workspace_id: workspaceId,
        product_id: productId,
        name: name.trim(),
        target_duration_s: duration,
      });
      close();
      // GA-X-02 — enter the guided wizard at step 2 (Kịch bản), exactly like
      // the mobile NewClipModal (B.5 entry-wiring). The picker lists READY
      // products; the ≥3-verified fact gate is still enforced server-side at
      // generate-creative (412).
      navigate(
        `/clip/new?projectId=${encodeURIComponent(created.id)}&productId=${encodeURIComponent(productId)}&step=2`,
      );
    } catch (err) {
      toast.error(
        t("projectsWeb.newSubmitFail", {
          error: err instanceof Error ? err.message : "",
        }),
      );
    }
  }

  const noReady = !products.isLoading && readyProducts.length === 0;
  const canSubmit =
    !!workspaceId && !!productId && name.trim().length >= 2 && !createProject.isPending;

  return (
    <IsaleModal
      visible={visible}
      onDismiss={() => {
        if (createProject.isPending) return;
        close();
      }}
      title={t("projectsWeb.newModalTitle")}
      actions={
        noReady
          ? [
              {
                label: t("projectsWeb.goToProducts"),
                variant: "primary",
                onPress: () => {
                  close();
                  navigate("/products");
                },
              },
              {
                label: t("common.cancel"),
                variant: "ghost",
                onPress: close,
              },
            ]
          : [
              {
                label: createProject.isPending
                  ? t("projectsWeb.newSubmitting")
                  : t("projects.newSubmitCta"),
                variant: "primary",
                onPress: handleSubmit,
                disabled: !canSubmit,
              },
              {
                label: t("common.cancel"),
                variant: "ghost",
                onPress: close,
                disabled: createProject.isPending,
              },
            ]
      }
    >
      {noReady ? (
        <div className="space-y-2">
          <p className="text-sm text-fg-muted">
            {t("projectsWeb.newNoReadyProducts")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="project-new-product"
              className="text-3xs font-extrabold uppercase tracking-wider text-fg-subtle"
            >
              {t("projectsWeb.newProductLabel")}
            </label>
            <select
              id="project-new-product"
              value={productId}
              onChange={(e) => handleProductSelect(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-default bg-elevated px-2 py-2 text-sm font-semibold text-fg outline-none"
            >
              <option value="">{t("projectsWeb.newProductPlaceholder")}</option>
              {readyProducts.map((p) => {
                const catLabel =
                  p.category === "fashion" ? t("products.catFashion")
                  : p.category === "food" ? t("products.catFood")
                  : p.category === "electronics" ? t("products.catElectronics")
                  : p.category === "home" ? t("products.catHome")
                  : t("products.catOther");
                return (
                  <option key={p.id} value={p.id}>
                    {p.name} · {catLabel}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label
              htmlFor="project-new-name"
              className="text-3xs font-extrabold uppercase tracking-wider text-fg-subtle"
            >
              {t("projects.newNameLabel")}
            </label>
            <input
              id="project-new-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects.newNamePlaceholder")}
              maxLength={200}
              className="mt-1 block w-full rounded-lg border border-default bg-elevated px-2 py-2 text-sm font-semibold text-fg outline-none"
            />
          </div>

          <div>
            <label className="text-3xs font-extrabold uppercase tracking-wider text-fg-subtle">
              {t("projects.newDurationLabel")}
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {([15, 30, 45, 60] as const).map((d) => {
                const active = duration === d;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDuration(d)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-xs font-bold transition",
                      active
                        ? "border-primary bg-primary text-fg-inverse"
                        : "border-default bg-elevated text-fg-muted",
                    )}
                  >
                    {d}s
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-fg-subtle">{t("projects.newCostHint")}</p>

          {!canSubmit && (
            <p className="text-center text-xs text-fg-muted">
              {t("projectsWeb.newSubmitHint")}
            </p>
          )}
        </div>
      )}
    </IsaleModal>
  );
}
